import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { matchRoutes, useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { appRoutes } from '../../routes/routeConfig'

const STORAGE_KEY = 'imperteks-tabs'
const MAX_TABS = 20
const LOGIN_PATH = '/login'

const TabsContext = createContext(null)
export const CurrentTabContext = createContext(null)

const matchRoute = (path) => (matchRoutes(appRoutes, path) || [])[0]

// /pattern/edit/53 -> 'Desen Düzenle #53'
export const getTabTitle = (path) => {
    const match = matchRoute(path)
    if (!match) return 'Sayfa'
    const ids = Object.values(match.params).filter(Boolean)
    return ids.length ? `${match.route.title} #${ids.join('/')}` : match.route.title
}

const newTabId = () => Math.random().toString(36).slice(2, 10)

const readStoredTabs = () => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY))
        return Array.isArray(stored)
            ? stored.filter(t => t.id && t.path).map(t => ({ ...t, back: t.back || [], version: 0 }))
            : []
    } catch {
        return []
    }
}

const writeStoredTabs = (tabs) => {
    try {
        if (tabs) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs.map(({ id, path, back, opener, openerPath }) => ({ id, path, back, opener, openerPath }))))
        else sessionStorage.removeItem(STORAGE_KEY)
    } catch { }
}

// Uygulama içi sekmeler: adres çubuğu her zaman aktif sekmenin sayfasını gösterir, açık sekmeler arka planda state'ini korur.
// Sekme: { id, path: gösterdiği sayfa, back: sekme içinde geri dönülecek sayfalar, opener: sekmeyi açan sekme }
export const TabsProvider = ({ enabled, children }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const activeKey = location.pathname + location.search
    // Yalnızca Geri butonu, kaydetme ve tarayıcının geri/ileri tuşu sekme içinde geri döner; menü tıklaması (PUSH) formu kapatmaz
    const isBackNavigation = useNavigationType() === 'POP' || location.state?.tabBack === true
    const [tabs, setTabs] = useState(() => enabled ? readStoredTabs() : [])
    const tabsRef = useRef(tabs)
    const activeIdRef = useRef()

    tabsRef.current = tabs

    // Adres her değiştiğinde hangi sekmenin gösterileceğine karar verilir
    useLayoutEffect(() => {
        if (!enabled || location.pathname === LOGIN_PATH) return
        const list = tabsRef.current
        const previous = list.find(t => t.id === activeIdRef.current)
        const existing = list.find(t => t.path === activeKey)
        let next = list
        let activeId
        let redirect

        if (existing) {
            activeId = existing.id
        } else if (previous && isBackNavigation && previous.back.includes(activeKey)) {
            // Sekme içinde geri dönüldü (Geri butonu, kaydetme veya tarayıcının geri tuşu)
            const back = previous.back.slice(0, previous.back.lastIndexOf(activeKey))
            next = list.map(t => t.id === previous.id ? { ...t, path: activeKey, back } : t)
            activeId = previous.id
        } else if (previous && matchRoute(activeKey)?.route.openInPlace) {
            next = list.map(t => t.id === previous.id ? { ...t, path: activeKey, back: [...t.back, t.path] } : t)
            activeId = previous.id
        } else {
            const owner = list.find(t => t.back.includes(activeKey))
            if (owner) {
                // Sayfa açık bir formun arkasında (bu veya başka bir sekmede), form kaybolmasın diye formda kalınır
                activeId = owner.id
                redirect = owner.path
            } else {
                const tab = { id: newTabId(), path: activeKey, back: [], opener: previous?.id, openerPath: previous?.path, version: 0 }
                next = [...list, tab]
                activeId = tab.id
            }
        }

        const now = Date.now()
        next = next.map(t => t.id === activeId ? { ...t, lastActive: now } : t)
        // Sınır aşılırsa en uzun süredir kullanılmayan sekme kapatılır
        while (next.length > MAX_TABS) {
            const oldest = next
                .filter(t => t.id !== activeId)
                .reduce((a, b) => (a.lastActive || 0) <= (b.lastActive || 0) ? a : b)
            next = next.filter(t => t !== oldest)
        }

        activeIdRef.current = activeId
        setTabs(next)
        if (redirect) navigate(redirect, { replace: true })
    }, [enabled, activeKey, location.pathname, isBackNavigation, navigate])

    useEffect(() => {
        if (!enabled) {
            setTabs(prev => prev.length ? [] : prev)
            writeStoredTabs(null)
            return
        }
        writeStoredTabs(tabs)
    }, [enabled, tabs])

    // Sayfalar başlığı açılırken kendileri ayarlıyor, gizli sekmeler aktif sekmenin başlığını ezmesin diye burada düzeltilir
    useEffect(() => {
        if (enabled) document.title = 'İmperteks / ' + getTabTitle(activeKey)
    }, [enabled, activeKey, tabs])

    const refreshTab = useCallback((id) => {
        setTabs(prev => prev.map(t => t.id === id ? { ...t, version: t.version + 1 } : t))
    }, [])

    // Sekme içinde açılmış formdan önceki sayfaya döner
    const goBack = useCallback((id) => {
        const tab = tabsRef.current.find(t => t.id === id)
        if (tab?.back.length) navigate(tab.back[tab.back.length - 1], { replace: true, state: { tabBack: true } })
    }, [navigate])

    const closeTab = useCallback((id, { refreshOpener = false } = {}) => {
        const list = tabsRef.current
        const index = list.findIndex(t => t.id === id)
        if (index === -1) return
        const closing = list[index]
        const rest = list.filter(t => t.id !== id)
        // Açan sekme bu arada başka bir sayfaya (ör. yeni kayıt formuna) geçtiyse yenilenmez, yoksa o form kaybolur
        setTabs(rest.map(t => refreshOpener && t.id === closing.opener && t.path === closing.openerPath ? { ...t, version: t.version + 1 } : t))
        if (id === activeIdRef.current) {
            // Önce sekmeyi açan sekmeye, o yoksa komşu sekmeye geçilir
            const next = rest.find(t => t.id === closing.opener) || rest[index] || rest[index - 1]
            navigate(next ? next.path : '/', { replace: true })
        }
    }, [navigate])

    const activeId = tabs.find(t => t.path === activeKey)?.id

    const value = useMemo(
        () => ({ tabs, activeId, closeTab, refreshTab, goBack }),
        [tabs, activeId, closeTab, refreshTab, goBack]
    )

    return (
        <TabsContext.Provider value={value}>
            {children}
        </TabsContext.Provider>
    )
}

export const useTabs = () => useContext(TabsContext)

// Kaydetme sonrası formu kapatır: form sekme içinde açıldıysa önceki sayfaya (liste) dönülür,
// ayrı sekmede açıldıysa sekme kapatılıp onu açan liste yenilenir
export const useCloseTab = () => {
    const tabs = useContext(TabsContext)
    const currentTab = useContext(CurrentTabContext)
    const navigate = useNavigate()

    return useCallback(() => {
        if (!tabs || !currentTab) return navigate(-1)
        const tab = tabs.tabs.find(t => t.id === currentTab.id)
        if (tab?.back.length) tabs.goBack(tab.id)
        else tabs.closeTab(currentTab.id, { refreshOpener: true })
    }, [tabs, currentTab, navigate])
}
